/**
 * ObjectiveGanttChart Component
 * 
 * D3-based Gantt chart for displaying objective timelines with focus item markers.
 */

import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';
import { Card, Tooltip, Empty, Switch, Typography } from 'antd';
import { GanttChartData, GanttRow, FocusMarker } from '../../../types/objective';
import styles from './ObjectiveGanttChart.module.css';

const { Text, Title } = Typography;

interface ObjectiveGanttChartProps {
  data: GanttChartData;
  height?: number;
  onRowClick?: (row: GanttRow) => void;
  className?: string;
}

// Color configuration by row type
const TYPE_COLORS: Record<string, { bar: string; background: string }> = {
  objective: { bar: '#1890ff', background: '#e6f7ff' },
  plan: { bar: '#722ed1', background: '#f9f0ff' },
  improvement: { bar: '#13c2c2', background: '#e6fffb' },
  operational: { bar: '#52c41a', background: '#f6ffed' },
};

// Status colors
const STATUS_COLORS: Record<string, string> = {
  validated: '#52c41a',
  completed: '#52c41a',
  in_progress: '#1890ff',
  active: '#faad14',
  not_started: '#d9d9d9',
};

// Focus marker colors by transition type
const FOCUS_MARKER_COLORS: Record<string, string> = {
  PROCESSITEM: '#ff4d4f',
  MATERIAL: '#faad14',
  CREATION: '#52c41a',
  RESOURCE: '#722ed1',
  default: '#1890ff',
};

export const ObjectiveGanttChart: React.FC<ObjectiveGanttChartProps> = ({
  data,
  height = 400,
  onRowClick,
  className,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height });
  const [showFocusMarkers, setShowFocusMarkers] = useState(true);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  // Parse dates and calculate timeline extent
  const { rows, timelineExtent } = useMemo(() => {
    const parsedRows = data.gantt_rows.map(row => ({
      ...row,
      startDate: row.start_date ? new Date(row.start_date) : null,
      endDate: row.end_date ? new Date(row.end_date) : null,
      focusMarkers: row.focus_markers.map(marker => ({
        ...marker,
        date: marker.date ? new Date(marker.date) : null,
      })),
    }));

    // Calculate time extent
    const allDates: Date[] = [];
    parsedRows.forEach(row => {
      if (row.startDate) allDates.push(row.startDate);
      if (row.endDate) allDates.push(row.endDate);
      row.focusMarkers.forEach(marker => {
        if (marker.date) allDates.push(marker.date);
      });
    });

    if (allDates.length === 0) {
      const now = new Date();
      return {
        rows: parsedRows,
        timelineExtent: [
          new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
          new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000),
        ] as [Date, Date],
      };
    }

    const minDate = d3.min(allDates) as Date;
    const maxDate = d3.max(allDates) as Date;
    
    // Add padding to timeline
    const padding = (maxDate.getTime() - minDate.getTime()) * 0.05;
    return {
      rows: parsedRows,
      timelineExtent: [
        new Date(minDate.getTime() - padding),
        new Date(maxDate.getTime() + padding),
      ] as [Date, Date],
    };
  }, [data.gantt_rows]);

  // Update dimensions on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        setDimensions({ width: Math.max(width, 400), height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [height]);

  // Draw the Gantt chart
  useEffect(() => {
    if (!svgRef.current || rows.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Margins and dimensions
    const margin = { top: 40, right: 20, bottom: 30, left: 200 };
    const chartWidth = dimensions.width - margin.left - margin.right;
    const rowHeight = 40;
    const chartHeight = Math.max(rows.length * rowHeight, 200);

    // Update SVG height
    svg.attr('height', chartHeight + margin.top + margin.bottom);

    // Create scales
    const xScale = d3.scaleTime()
      .domain(timelineExtent)
      .range([0, chartWidth]);

    const yScale = d3.scaleBand<number>()
      .domain(rows.map((_, i) => i))
      .range([0, chartHeight])
      .padding(0.2);

    // Create main group
    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Add grid lines
    const xAxisGrid = d3.axisBottom(xScale)
      .tickSize(chartHeight)
      .tickFormat(() => '');

    g.append('g')
      .attr('class', styles.gridLines)
      .call(xAxisGrid)
      .selectAll('line')
      .attr('stroke', '#e8e8e8')
      .attr('stroke-dasharray', '2,2');

    // Add x-axis
    const xAxis = d3.axisTop(xScale)
      .tickFormat(d => d3.timeFormat('%b %d')(d as Date));

    g.append('g')
      .attr('class', styles.xAxis)
      .call(xAxis)
      .selectAll('text')
      .attr('font-size', '11px');

    // Add today line
    const today = new Date();
    if (today >= timelineExtent[0] && today <= timelineExtent[1]) {
      g.append('line')
        .attr('class', styles.todayLine)
        .attr('x1', xScale(today))
        .attr('x2', xScale(today))
        .attr('y1', 0)
        .attr('y2', chartHeight)
        .attr('stroke', '#ff4d4f')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '4,4');

      g.append('text')
        .attr('x', xScale(today))
        .attr('y', -10)
        .attr('text-anchor', 'middle')
        .attr('font-size', '10px')
        .attr('fill', '#ff4d4f')
        .text('Today');
    }

    // Add row labels (left side)
    const labels = svg.append('g')
      .attr('transform', `translate(0,${margin.top})`);

    rows.forEach((row, i) => {
      const y = yScale(i) ?? 0;
      const colors = TYPE_COLORS[row.type] || TYPE_COLORS.operational;
      const indent = row.level * 12;

      // Label background
      labels.append('rect')
        .attr('x', 0)
        .attr('y', y)
        .attr('width', margin.left - 10)
        .attr('height', yScale.bandwidth())
        .attr('fill', hoveredRow === row.id ? colors.background : 'transparent')
        .attr('rx', 4);

      // Type indicator
      labels.append('rect')
        .attr('x', indent + 5)
        .attr('y', y + (yScale.bandwidth() - 12) / 2)
        .attr('width', 4)
        .attr('height', 12)
        .attr('fill', colors.bar)
        .attr('rx', 2);

      // Label text
      labels.append('text')
        .attr('x', indent + 15)
        .attr('y', y + yScale.bandwidth() / 2)
        .attr('dy', '0.35em')
        .attr('font-size', '12px')
        .attr('font-weight', row.level === 0 ? '600' : '400')
        .attr('fill', '#262626')
        .text(row.name.length > 20 ? row.name.substring(0, 20) + '...' : row.name)
        .style('cursor', 'pointer')
        .on('click', () => onRowClick?.(row));
    });

    // Add bars
    const bars = g.append('g').attr('class', 'bars');

    rows.forEach((row, i) => {
      const y = yScale(i) ?? 0;
      const barHeight = yScale.bandwidth();
      const colors = TYPE_COLORS[row.type] || TYPE_COLORS.operational;
      const statusColor = STATUS_COLORS[row.status] || STATUS_COLORS.active;

      // Only draw bar if we have valid dates
      if (row.startDate && row.endDate) {
        const x1 = xScale(row.startDate);
        const x2 = xScale(row.endDate);
        const barWidth = Math.max(x2 - x1, 4);

        // Background bar
        bars.append('rect')
          .attr('x', x1)
          .attr('y', y)
          .attr('width', barWidth)
          .attr('height', barHeight)
          .attr('fill', colors.background)
          .attr('stroke', colors.bar)
          .attr('stroke-width', 1)
          .attr('rx', 4)
          .style('cursor', 'pointer')
          .on('mouseenter', () => setHoveredRow(row.id))
          .on('mouseleave', () => setHoveredRow(null))
          .on('click', () => onRowClick?.(row));

        // Progress bar
        if (row.progress > 0) {
          bars.append('rect')
            .attr('x', x1)
            .attr('y', y)
            .attr('width', (barWidth * row.progress) / 100)
            .attr('height', barHeight)
            .attr('fill', statusColor)
            .attr('opacity', 0.7)
            .attr('rx', 4)
            .style('pointer-events', 'none');
        }

        // Progress text
        bars.append('text')
          .attr('x', x1 + barWidth / 2)
          .attr('y', y + barHeight / 2)
          .attr('dy', '0.35em')
          .attr('text-anchor', 'middle')
          .attr('font-size', '10px')
          .attr('font-weight', '600')
          .attr('fill', row.progress > 50 ? '#fff' : '#262626')
          .text(`${row.progress}%`)
          .style('pointer-events', 'none');

        // Focus markers
        if (showFocusMarkers && row.focusMarkers.length > 0) {
          row.focusMarkers.forEach((marker) => {
            if (marker.date) {
              const markerX = xScale(marker.date);
              const markerColor = FOCUS_MARKER_COLORS[marker.type] || FOCUS_MARKER_COLORS.default;

              // Marker diamond
              bars.append('path')
                .attr('d', d3.symbol().type(d3.symbolDiamond).size(60))
                .attr('transform', `translate(${markerX},${y + barHeight / 2})`)
                .attr('fill', markerColor)
                .attr('stroke', '#fff')
                .attr('stroke-width', 1)
                .style('cursor', 'help')
                .append('title')
                .text(`${marker.from_state} → ${marker.to_state}`);
            }
          });
        }
      }
    });

  }, [rows, dimensions, timelineExtent, showFocusMarkers, hoveredRow, onRowClick]);

  if (rows.length === 0) {
    return (
      <Card className={`${styles.chartContainer} ${className || ''}`} bordered={false}>
        <Empty description="No timeline data available" />
      </Card>
    );
  }

  return (
    <Card className={`${styles.chartContainer} ${className || ''}`} bordered={false}>
      <div className={styles.chartHeader}>
        <Title level={5} className={styles.chartTitle}>
          Project Timeline
        </Title>
        <div className={styles.chartControls}>
          <Text type="secondary" className={styles.controlLabel}>Show Focus Items</Text>
          <Switch 
            checked={showFocusMarkers} 
            onChange={setShowFocusMarkers}
            size="small"
          />
        </div>
      </div>
      
      <div className={styles.legendRow}>
        {Object.entries(TYPE_COLORS).map(([type, colors]) => (
          <div key={type} className={styles.legendItem}>
            <span 
              className={styles.legendColor} 
              style={{ backgroundColor: colors.bar }}
            />
            <span className={styles.legendText}>{type}</span>
          </div>
        ))}
      </div>

      <div ref={containerRef} className={styles.chartWrapper}>
        <svg ref={svgRef} width={dimensions.width} />
      </div>

      {showFocusMarkers && (
        <div className={styles.markerLegend}>
          <Text type="secondary" className={styles.markerLegendTitle}>
            Focus Items (State Transitions):
          </Text>
          {Object.entries(FOCUS_MARKER_COLORS).filter(([key]) => key !== 'default').map(([type, color]) => (
            <div key={type} className={styles.markerLegendItem}>
              <span 
                className={styles.markerDiamond} 
                style={{ backgroundColor: color }}
              />
              <span className={styles.markerText}>{type}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default ObjectiveGanttChart;
